import { TBackofficeGroupResourceItem } from '@/backoffice/features/groups/hooks/use-backoffice-group-resource-update-promises.types';
import { isBackofficeGroupResourceItem } from '@/backoffice/features/groups/hooks/use-backoffice-group-resource-update-promises.utils';
import {
  useUpdateBackofficeResourceGroupMutation,
  useUpdateBackofficeTenantMutation,
  useUpdateSuperUserMutation,
  useUpdateSuperUserRoleMutation,
} from '@/backoffice/redux/api/backoffice/backoffice-api';
import {
  TGroupResourceItem,
  TResourceUpdatePromises,
} from '@/shared/features/groups/context/groups-config-context.types';

// Re-parents a resource by __typename (empty groupId/parentGroupId = ungroup).
export const useBackofficeGroupResourceUpdatePromises = () => {
  const [updateUser] = useUpdateSuperUserMutation();
  const [updateRole] = useUpdateSuperUserRoleMutation();
  const [updateGroup] = useUpdateBackofficeResourceGroupMutation();
  const [updateTenant] = useUpdateBackofficeTenantMutation();

  const createBackofficeResourceUpdatePromises = (
    resources: TBackofficeGroupResourceItem[],
    groupId: string,
  ): Promise<unknown>[] => {
    const promises: Promise<unknown>[] = [];

    resources.forEach((item) => {
      if (item.__typename === 'SuperUserResponseType') {
        promises.push(
          updateUser({
            userId: item.userId,
            user: { groupId, objectVersion: item.objectVersion! + 1 },
          }).unwrap(),
        );
        return;
      }

      if (item.__typename === 'SuperUserRoleResponseType') {
        promises.push(
          updateRole({
            userRoleId: item.userRoleId,
            userRole: { groupId, objectVersion: item.objectVersion! + 1 },
          }).unwrap(),
        );
        return;
      }

      if (item.__typename === 'BackofficeResourceGroupResponseType') {
        promises.push(
          updateGroup({
            groupId: item.groupId,
            resourceGroup: {
              parentGroupId: groupId,
              objectVersion: item.objectVersion! + 1,
            },
          }).unwrap(),
        );
        return;
      }

      if (item.__typename === 'TenantResponseType') {
        promises.push(
          updateTenant({
            tenantId: item.tenantId,
            tenant: { groupId, objectVersion: item.objectVersion! + 1 },
          }).unwrap(),
        );
      }
    });

    return promises;
  };

  const createResourceUpdatePromises: TResourceUpdatePromises['createResourceUpdatePromises'] =
    (resources: TGroupResourceItem[], groupId: string) =>
      createBackofficeResourceUpdatePromises(
        resources.filter(isBackofficeGroupResourceItem),
        groupId,
      );

  return { createResourceUpdatePromises };
};
